import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'

/**
 * TiltCard — a subtle 3D tilt-toward-cursor wrapper with a soft glow that
 * follows the pointer. Premium/futuristic feel for card grids.
 *
 * Safe by default: disabled on coarse pointers (touch) and when the user
 * prefers reduced motion — it then renders a plain interactive container.
 */
interface TiltCardProps {
  children: ReactNode
  className?: string
  onClick?: () => void
  /** Max tilt in degrees (each axis). */
  maxTilt?: number
  /** Glow colour that tracks the cursor. */
  glowColor?: string
}

function tiltDisabled(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return (
    window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function TiltCard({
  children,
  className,
  onClick,
  maxTilt = 7,
  glowColor = 'rgba(255, 255, 255, 0.18)',
}: TiltCardProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const disabledRef = useRef<boolean | null>(null)
  const [transform, setTransform] = useState('perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)')
  const [transition, setTransition] = useState('transform 0.4s ease')
  const [glow, setGlow] = useState<CSSProperties>({ opacity: 0 })

  const handleMove = (e: PointerEvent<HTMLDivElement>) => {
    if (disabledRef.current === null) disabledRef.current = tiltDisabled()
    if (disabledRef.current) return
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const px = (e.clientX - r.left) / r.width
    const py = (e.clientY - r.top) / r.height
    const ry = (px - 0.5) * 2 * maxTilt
    const rx = -(py - 0.5) * 2 * maxTilt
    setTransition('transform 0.08s ease-out')
    setTransform(`perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(1.02)`)
    setGlow({
      opacity: 1,
      background: `radial-gradient(220px circle at ${(px * 100).toFixed(1)}% ${(py * 100).toFixed(1)}%, ${glowColor}, transparent 60%)`,
    })
  }

  const reset = () => {
    setTransition('transform 0.4s ease')
    setTransform('perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)')
    setGlow({ opacity: 0 })
  }

  const handleKey = onClick
    ? (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }
    : undefined

  return (
    <div
      ref={ref}
      className={className}
      style={{ transform, transition, willChange: 'transform' }}
      onPointerMove={handleMove}
      onPointerLeave={reset}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKey}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          transition: 'opacity 0.2s ease',
          ...glow,
        }}
      />
      {children}
    </div>
  )
}

export default TiltCard
